import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { useCompanyGetNfcTagsQuery, useNfcTagConnectToUserMutation } from '../../../common/redux/api/web-api'
import { Table, TableBody, TableHeader, TableBodyCell, TableRow, TableHeaderCell } from '../../../lib/table'
import { notifySuccess, notifyError } from '../../../common/features/notificationsManager/Notify'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { WidgetLayout } from '../../../lib/widget-layout'
import { Button } from '../../../common/components/Button'
import { RootState } from '../../redux/store'
import Text from '../../../ui/Text'
import useText from '../../../common/hooks/useText'
import { useNavigate, useParams } from 'react-router-dom'

const ButtonContainer = styled.div`
    margin-bottom: 20px;
    justify-content: flex-end;
    display: flex;
    gap: 4px;
`

const InputContainer = styled.div`
    max-width: 300px;
    margin-bottom: 20px;
`

export const UserNfcTagsPage = React.memo(function UserNfcTagsPage(): React.ReactElement {
    const text = useText()
    const { id: userId } = useParams()
    const navigate = useNavigate()
    const [searchFilter, setSearchFilter] = useState('')
    const [buttonLoading, setButtonLoading] = useState('')

    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)

    const { data: nfcTags, isLoading } = useCompanyGetNfcTagsQuery({ companyId }, { refetchOnMountOrArgChange: true })
    const [connectNfcToUser] = useNfcTagConnectToUserMutation()

    const addToUser = async (tagId: string) => {
        setButtonLoading(tagId)
        try {
            await connectNfcToUser({ nfcTagId: tagId, userId }).unwrap()
            notifySuccess(text('msgRFIDTagAddedToUser'), true)
            navigate(`/user/${userId}`)
        } catch (e) {
            console.error(e)
            notifyError(text('msgRFIDTagCouldNotBeAdded'), true)
            setButtonLoading('')
        }
    }

    return (
        <PageLayoutContainer>
            <ButtonContainer>
                <Button
                    size="sm"
                    label={text('btnCancel')}
                    onClick={() => navigate(`/user/${userId}`)}
                    data-testid="btnCancel"
                ></Button>
            </ButtonContainer>
            <WidgetLayout title={text('lblRFIDPage')}>
                <InputContainer>
                    <input
                        type="text"
                        name="search"
                        id="search"
                        placeholder={text('msgSearch')}
                        className="form-control"
                        onChange={(e) => setSearchFilter(e.target.value)}
                    />
                </InputContainer>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell data-testid="h-cell-id">
                                <Text code="lblId" />
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="h-cell-comment">
                                <Text code="lblComment" />
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="h-cell-user"></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {nfcTags
                            ?.filter((tag) => {
                                const search = searchFilter.toString().toLocaleLowerCase()
                                return (
                                    tag.id?.toString().toLocaleLowerCase().includes(search) ||
                                    tag.comment?.toLowerCase()?.includes(search) ||
                                    tag.firstName?.toLowerCase()?.includes(search) ||
                                    tag.lastName?.toLowerCase()?.includes(search)
                                )
                            })
                            ?.map((tag) => (
                                <TableRow key={tag.id}>
                                    <TableBodyCell data-testid="b-cell-id">{tag?.id}</TableBodyCell>
                                    <TableBodyCell data-testid="b-cell-comment">{tag?.comment}</TableBodyCell>
                                    <TableBodyCell data-testid="b-cell-id">
                                        {tag?.firstName ? (
                                            `${tag.firstName} ${tag.lastName}`
                                        ) : (
                                            <Button
                                                size="sm"
                                                label={text('btnAddToUser')}
                                                onClick={() => tag.id && addToUser(tag.id)}
                                                loading={buttonLoading === tag.id}
                                                data-testid="add-to-user-btn"
                                            />
                                        )}
                                    </TableBodyCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                {isLoading && <div className="shimmer-container shimmer-small"></div>}
            </WidgetLayout>
        </PageLayoutContainer>
    )
})
